// Receipt image drawer functionality
import { logMessage, parseUrlForGroupId } from './utils.mjs';
import { editExpense, getExpenseImageUrl } from './api.mjs';
import { cloneItemizedData } from './receiptHelpers.mjs';

// Update receipt visibility
export async function updateReceiptVisibility(expense, isVisible, participants) {
    const groupId = parseUrlForGroupId();
    if (!groupId) return;

    // Deep copy & strip typename
    const itemizedDataInput = cloneItemizedData(expense.itemizedData);

    itemizedDataInput.receiptAvailable = isVisible;

    try {
        logMessage('Updating visibility...', 'info');
        const updatedGroup = await editExpense(groupId, expense.id, {
            itemizedData: itemizedDataInput,
            expenseType: expense.expenseType,
            currency: expense.currency || 'USD',
            date: expense.date,
            description: expense.description
        });
        logMessage('Visibility updated', 'success');

        // Refresh view to show/hide image
        const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
        if (updatedExpense) {
            const { renderReceiptView } = await import('./receiptView.mjs');
            renderReceiptView(updatedExpense, participants, false);
        }
    } catch (e) {
        console.error(e);
        logMessage('Error updating visibility: ' + e.message, 'error');
    }
}

// Open Receipt Drawer
export function openReceiptDrawer(expense, participants) {
    const overlay = document.createElement('div');
    overlay.className = 'bottom-sheet-overlay';

    const content = document.createElement('div');
    content.className = 'bottom-sheet-content';

    // --- Header Section (Fixed) ---
    const headerSection = document.createElement('div');
    headerSection.className = 'sheet-header-section';

    // Handle
    const handle = document.createElement('div');
    handle.className = 'sheet-handle-bar';
    headerSection.appendChild(handle);

    // Visibility Toggle - Only if Owner
    const isReceiptAvailable = expense.itemizedData.receiptAvailable === true;

    // Replicating Flutter logic: user != null && user.email == expense.itemizedData?.owner
    const userEmail = localStorage.getItem('splitpal_user_email');
    const isOwner = userEmail && userEmail === expense.itemizedData?.owner;

    if (isOwner) {
        const visibilityDiv = document.createElement('div');
        visibilityDiv.style.width = '100%';
        visibilityDiv.style.marginBottom = '10px'; // Reduced slightly as padding adds up

        visibilityDiv.innerHTML = `
            <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;">
                <div>
                    <div style="font-size: 16px; font-weight: 500; margin-bottom: 4px;">Image Visibility</div>
                    <div style="font-size: 13px; color: #666; line-height: 1.4;">Anyone with a link to this group will be able to view this receipt image.</div>
                </div>
                <label class="toggle-switch" style="flex-shrink: 0;">
                    <input type="checkbox" ${isReceiptAvailable ? 'checked' : ''}>
                    <span class="slider"></span>
                </label>
            </div>
        `;

        const checkbox = visibilityDiv.querySelector('input');
        checkbox.addEventListener('change', async (e) => {
            const newState = e.target.checked;
            await updateReceiptVisibility(expense, newState, participants);
        });

        headerSection.appendChild(visibilityDiv);
    }

    content.appendChild(headerSection);

    // --- Scrollable Body ---
    const scrollBody = document.createElement('div');
    scrollBody.className = 'sheet-scroll-body';

    // Image Container
    // Logic: Viewable if public OR owner


    if (isReceiptAvailable || isOwner) {
        const imgContainer = document.createElement('div');
        imgContainer.className = 'receipt-image-container';
        imgContainer.style.backgroundColor = 'transparent'; // Transparent background
        imgContainer.style.minHeight = '300px';

        const loadingText = document.createElement('div');
        loadingText.innerText = 'Loading image...';
        loadingText.style.color = '#888';
        loadingText.style.padding = '20px';
        imgContainer.appendChild(loadingText);
        scrollBody.appendChild(imgContainer);

        // Fetch image logic with Base64 caching
        const groupId = parseUrlForGroupId();
        if (groupId) {
            const cacheKey = `receipt_base64_${groupId}_${expense.id}`;
            const cachedBase64 = localStorage.getItem(cacheKey);

            if (cachedBase64) {
                // Use cached image data
                const img = document.createElement('img');
                img.className = 'receipt-image';
                img.style.borderRadius = '12px';
                img.src = cachedBase64;
                imgContainer.innerHTML = '';
                imgContainer.appendChild(img);
            } else {
                // Fetch fresh URL and then data
                getExpenseImageUrl(groupId, expense.id).then(async url => {
                    if (!url) {
                        imgContainer.innerHTML = '<div style="padding: 20px; color: #888; text-align: center;">Image not available</div>';
                        return;
                    }

                    try {
                        // Fetch the actual image data
                        const response = await fetch(url);
                        if (!response.ok) throw new Error('Network response was not ok');
                        const blob = await response.blob();

                        // Convert to Base64
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const base64data = reader.result;

                            // Display
                            imgContainer.innerHTML = '';
                            const img = document.createElement('img');
                            img.src = base64data;
                            img.className = 'receipt-image';
                            img.style.borderRadius = '12px';
                            imgContainer.appendChild(img);

                            // Cache
                            try {
                                localStorage.setItem(cacheKey, base64data);
                            } catch (e) {
                                console.warn('Failed to cache receipt image (likely quota exceeded)', e);
                            }
                        };
                        reader.readAsDataURL(blob);

                    } catch (e) {
                        console.error('Failed to fetch/convert image for caching', e);
                        // Fallback: just show the URL directly
                        imgContainer.innerHTML = '';
                        const img = document.createElement('img');
                        img.src = url;
                        img.className = 'receipt-image';
                        img.style.borderRadius = '12px';
                        imgContainer.appendChild(img);
                    }
                });
            }
        }
    } else {
        const noImageMsg = document.createElement('div');
        noImageMsg.style.textAlign = 'center';
        noImageMsg.style.padding = '40px';
        noImageMsg.style.color = '#888';
        noImageMsg.textContent = 'Receipt image is hidden.';
        scrollBody.appendChild(noImageMsg);
    }

    content.appendChild(scrollBody);
    overlay.appendChild(content);
    document.body.appendChild(overlay);

    // Trigger animation
    requestAnimationFrame(() => {
        overlay.classList.add('show');
    });

    // Close logic
    const closeSheet = () => {
        document.body.style.overflow = ''; // Restore scroll
        overlay.classList.remove('show');
        content.style.transform = 'translateX(-50%) translateY(100%)';
        setTimeout(() => {
            overlay.remove();
        }, 300);
    };

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            closeSheet();
        }
    });

    // Prevent background interaction
    document.body.style.overflow = 'hidden';
}

// Generate QR Code SVG string
export function generateQRCodeSVG(data) {
    try {
        const typeNumber = 0; // auto
        const errorCorrectionLevel = 'L';
        const qr = qrcode(typeNumber, errorCorrectionLevel);
        qr.addData(data);
        qr.make();
        // Generate SVG version (using 4 as cell size, 2 as margin)
        let svg = qr.createSvgTag(4, 2);
        // Make background transparent by removing fill or setting it to transparent
        svg = svg.replace(/fill="white"/g, 'fill="transparent"');
        svg = svg.replace(/fill="#ffffff"/gi, 'fill="transparent"');
        return svg;
    } catch (e) {
        console.error('Error generating QR code', e);
        return '<div>Error generating QR code</div>';
    }
}

